import React, { useState, useEffect } from 'react';
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  FileText,
  Receipt,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from '../../firebase.config';
import { formatCurrency, formatDate, CollapsibleQuickTotals, ReportHeaderWithExport } from './CommonComponents';
import { getCollectionForCompany } from '../../utils/dataAdapter';
import { useCompanyCurrency } from '../../hooks/useCompanyCurrency';
import { formatCurrencyIntl } from '../../utils/currencyUtils';

const PartywiseSalesReport = ({ db, userId, appId, companyId, dateRange, selectedParty, parties, loading, setLoading, companyDetails }) => {
  const { currency: companyCurrency } = useCompanyCurrency();
  const [reportData, setReportData] = useState([]);
  const [totalSummary, setTotalSummary] = useState({
    totalInvoices: 0,
    totalAmount: 0,
    totalGST: 0,
    totalPaid: 0,
    totalOutstanding: 0
  });
  const [expandedParty, setExpandedParty] = useState(null);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [itemNames, setItemNames] = useState({});
  const [itemMap, setItemMap] = useState({});

  // ESC key handler for closing expansions
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        // Close invoice expansion first, then party expansion
        if (expandedInvoice) {
          setExpandedInvoice(null);
          return; // Stop here, don't close other expansion
        }
        if (expandedParty) {
          setExpandedParty(null);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [expandedInvoice, expandedParty]);

  // Normalize party id that may be saved as a path like "parties/{id}" or a document ref-like string
  const normalizePartyId = (raw) => {
    if (!raw) return '';
    let val = raw;
    if (typeof val === 'object') {
      // Some systems store references; try common shapes
      val = val.id || val._key || val._path || String(val);
    }
    if (typeof val === 'string' && val.includes('/')) {
      const parts = val.split('/');
      return parts[parts.length - 1];
    }
    return String(val);
  };
  

  const partyIdToName = React.useMemo(() => {
    const map = {};
    (parties || []).forEach(p => {
      const partyId = p.id;
      map[partyId] = p.firmName || p.name || p.partyName || p.displayName || p.id;
      // Also map normalized versions (in case partyId is stored as a path)
      if (partyId && typeof partyId === 'string' && partyId.includes('/')) {
        const normalizedId = partyId.split('/').pop();
        if (normalizedId && !map[normalizedId]) {
          map[normalizedId] = p.firmName || p.name || p.partyName || p.displayName || normalizedId;
        }
      }
    });
    return map;
  }, [parties]);

  // Function to fetch party name from Firestore if not in map
  const fetchPartyName = async (partyId) => {
    if (!partyId || !db || !companyId || !appId || !userId) {
      return partyId || 'Unknown';
    }
    
    try {
      const partiesCollection = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      const partyRef = doc(db, partiesCollection.path, partyId);
      const partySnap = await getDoc(partyRef);
      
      if (partySnap.exists()) {
        const partyData = partySnap.data();
        return partyData.firmName || partyData.name || partyData.partyName || partyData.displayName || partyId;
      }
    } catch (error) {
      console.error(`Error fetching party ${partyId}:`, error);
    }
    
    return partyId || 'Unknown';
  };

  // Function to calculate GST for an invoice using rows and item master
  const calculateGST = (rows, effectiveCompanyDetails, effectiveItemMap) => {
    
    // Always calculate GST from rows first (regardless of company status)
    let totalSGST = 0, totalCGST = 0, totalIGST = 0;
    
    if (Array.isArray(rows)) {
      rows.forEach((row, index) => {
        const itemId = row.item || row.id || row.itemId || row.productId;
        const itemDef = effectiveItemMap[itemId] || {};
        const qty = Number(row.qty || row.quantity || 0);
        const rate = Number(row.rate || row.price || 0);
        const taxable = Number(row.amount || row.total || (qty && rate ? qty * rate : 0) || 0);

        // Determine company GST mode
        const gstType = (effectiveCompanyDetails?.gstinType || effectiveCompanyDetails?.gstType || 'regular').toLowerCase();

        let sgst = 0, cgst = 0, igst = 0;

        if (gstType === 'composition') {
          const itemType = (itemDef.itemType || itemDef.type || '').toLowerCase();
          const compRate = Number(itemDef.compositionGstRate ?? (itemType === 'services' ? 6 : itemType === 'restaurant' || itemType === 'restaurants' ? 5 : 1));
          const tax = taxable * (compRate / 100);
          sgst = tax / 2;
          cgst = tax / 2;
          igst = 0;
        } else {
          // Regular mode: derive rate from row percents or item master
          const rowRateFromPercents = Number(row.sgst || 0) + Number(row.cgst || 0) + Number(row.igst || 0);
          const effectiveRate = rowRateFromPercents > 0 ? rowRateFromPercents : Number(row.gstPercent ?? itemDef.gstPercentage ?? 0);
          const tax = taxable * (effectiveRate / 100);
          if (Number(row.igst || 0) > 0) {
            igst = tax;
          } else {
            sgst = tax / 2;
            cgst = tax / 2;
          }
        }

        totalSGST += sgst;
        totalCGST += cgst;
        totalIGST += igst;

      });
    }
    
    const totalGST = totalSGST + totalCGST + totalIGST;
    
    // If company details are available, apply business rules
    if (effectiveCompanyDetails) {
      const gstStatus = effectiveCompanyDetails.gstStatus || effectiveCompanyDetails.registrationStatus || 'registered';
      const gstType = (effectiveCompanyDetails.gstinType || effectiveCompanyDetails.gstType || 'regular').toLowerCase();

      // Not registered or unregistered - no GST
      if (gstStatus === 'unregistered' || gstStatus === 'not_registered') {
        return { totalSGST: 0, totalCGST: 0, totalIGST: 0, totalGST: 0 };
      }

      // Composition scheme - no GST collection on individual items
      if (gstType === 'composition') {
        // Compute composition tax based on item-wise composition rate
        let compTax = 0;
        if (Array.isArray(rows)) {
          rows.forEach((row) => {
            const taxable = Number(row.amount || row.total || (row.qty || row.quantity || 0) * (row.rate || row.price || 0) || 0);
            const itemId = row.item || row.id || row.itemId || row.productId;
            const itemDef = effectiveItemMap[itemId] || {};
            const itemType = (itemDef.itemType || itemDef.type || '').toLowerCase();
            const rateFromItem = Number(itemDef.compositionGstRate ?? (
              itemType === 'services' ? 6 : itemType === 'restaurant' || itemType === 'restaurants' ? 5 : 1
            ));
            const lineTax = taxable * (rateFromItem / 100);
            compTax += lineTax;
          });
        }
        const half = compTax / 2;
        return { totalSGST: half, totalCGST: half, totalIGST: 0, totalGST: compTax };
      }
    }
    
    // Return calculated GST (default behavior)
    return { totalSGST, totalCGST, totalIGST, totalGST };
  };

  // Function to fetch item names from items collection
  const fetchItemNames = async (itemIds) => {
    if (!itemIds || itemIds.length === 0) return {};
    
    try {
      const itemNames = {};
      
      // Use the same collection path as the rest of the app
      const itemsCollection = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
      if (!itemsCollection) {
        console.error('Items collection not found');
        return {};
      }
      
      // Fetch items one by one to avoid Firestore 'in' query limitations
      for (const itemId of itemIds) {
        try {
          const itemRef = doc(db, itemsCollection.path, itemId);
          const itemSnap = await getDoc(itemRef);
          if (itemSnap.exists()) {
            const data = itemSnap.data();
            itemNames[itemId] = data.name || data.itemName || data.productName || itemId;
          } else {
            itemNames[itemId] = itemId; // Fallback to ID if item not found
          }
        } catch (itemError) {
          console.error(`Error fetching item ${itemId}:`, itemError);
          itemNames[itemId] = itemId; // Fallback to ID if error
        }
      }
      
      return itemNames;
    } catch (error) {
      console.error('Error fetching item names:', error);
      return {};
    }
  };

  useEffect(() => {
    if (!db || !userId || !appId || !companyId) {
      return;
    }
    setLoading(true);
    const fetchData = async () => {
      // Use company details from props for GST calculation
      let fetchedCompanyDetails = companyDetails || null;

      // Fetch items map for GST rates and names
      try {
        const itemsSnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'items'));
        const map = {};
        itemsSnap.forEach(docSnap => {
          const data = docSnap.data();
          map[docSnap.id] = {
            name: data.name || data.itemName || data.productName,
            gstPercentage: Number(data.gstPercentage ?? data.taxRate ?? 0),
            compositionGstRate: Number(data.compositionGstRate ?? data.compGstRate ?? undefined),
            itemType: data.itemType || data.type || ''
          };
        });
        setItemMap(map);
      } catch (error) {
        console.error('Error fetching items for GST map:', error);
      }
      
      try {
        // Fetch sales from company-scoped collection with date filtering
        const salesCollection = getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills');
        
        // Convert date range to string format for Firestore query
        const startStr = new Date(dateRange.start).toISOString().split('T')[0];
        const endStr = new Date(dateRange.end).toISOString().split('T')[0];
        
        console.log('📊 PARTYWISE SALES DEBUG - Date Range:', {
          start: startStr,
          end: endStr,
          startDate: dateRange.start,
          endDate: dateRange.end
        });
        
        let salesQueryRef;
        let salesSnapshot;
        
        try {
          // Try with date filtering first
          salesQueryRef = query(
            salesCollection,
            where('invoiceDate', '>=', startStr),
            where('invoiceDate', '<=', endStr),
            orderBy('invoiceDate', 'desc')
          );
          salesSnapshot = await getDocs(salesQueryRef);
          console.log('📊 PARTYWISE SALES DEBUG - Query with date filter successful');
        } catch (error) {
          console.log('📊 PARTYWISE SALES DEBUG - Date filter query failed, trying without date filter:', error);
          // Fallback: get all sales and filter in JavaScript
          salesQueryRef = query(salesCollection, orderBy('invoiceDate', 'desc'));
          salesSnapshot = await getDocs(salesQueryRef);
        }
        
        console.log('📊 PARTYWISE SALES DEBUG - Raw Data:', {
          totalDocs: salesSnapshot.docs.length,
          sampleDoc: salesSnapshot.docs[0]?.data(),
          dateRangeQuery: { startStr, endStr },
          actualDateRange: {
            start: dateRange.start.toISOString().split('T')[0],
            end: dateRange.end.toISOString().split('T')[0]
          }
        });
        
        // Show sample invoice dates to debug format issues
        if (salesSnapshot.docs.length > 0) {
          console.log('📊 PARTYWISE SALES DEBUG - Sample Invoice Dates:', 
            salesSnapshot.docs.slice(0, 5).map(doc => ({
              id: doc.id,
              invoiceDate: doc.data().invoiceDate,
              date: doc.data().date,
              createdDate: doc.data().createdDate,
              allDateFields: Object.keys(doc.data()).filter(key => 
                key.toLowerCase().includes('date') || key.toLowerCase().includes('time')
              )
            }))
          );
        }
        
        let sales = salesSnapshot.docs.map(doc => {
          const data = doc.data();
          
          // Parse invoiceDate as Date
          let saleDate = data.invoiceDate;
          let saleDateObj = saleDate ? new Date(saleDate) : null;
          
          // Try to get partyId from customFields or party field and normalize
          let partyId = normalizePartyId(data.customFields?.party || data.party || data.partyId || '');
          
          // Resolve party name using map with fallbacks
          let partyName = partyIdToName[partyId] || data.partyName || data.customerName || partyId || 'Unknown';
          
          // Try to get invoice number from multiple fields
          let invoiceNumber = data.customFields?.number || data.invoiceNumber || data.billNumber || data.number || data.invoiceNo || doc.id;
          
          // FIX: Handle advance invoices (ADV-INV) as negative amounts
          let baseAmount = Number(data.amount) || 0;
          let isAdvanceInvoice = invoiceNumber && invoiceNumber.startsWith('ADV-INV');
          let totalAmount = isAdvanceInvoice ? -baseAmount : baseAmount; // Negative for advance invoices
          
          // GST calculation based on company registration status
           const { totalSGST, totalCGST, totalIGST, totalGST } = calculateGST(data.rows, fetchedCompanyDetails, itemMap);
          
          console.log('📊 PARTYWISE SALES DEBUG - Processed Sale:', {
            docId: doc.id,
            invoiceNumber,
            partyId,
            partyName,
            invoiceDate: data.invoiceDate,
            amount: totalAmount,
            gst: totalGST
          });
          
          return {
            id: doc.id,
            partyId,
            partyName,
            totalAmount: totalAmount,
            sgst: totalSGST,
            cgst: totalCGST,
            igst: totalIGST,
            gst: totalGST,
            date: saleDateObj,
            invoiceNumber,
            items: data.rows || [],
            payments: data.payments || [],
            isAdvanceInvoice: isAdvanceInvoice,
            ...data
          };
        });
        
        console.log('📊 PARTYWISE SALES DEBUG - After Processing:', {
          totalSales: sales.length,
          sampleSale: sales[0]
        });
        
        // Additional JavaScript date filtering as fallback
        const originalSalesCount = sales.length;
        console.log('📊 JS FILTER DEBUG - Date Range for Filter:', {
          start: dateRange.start,
          end: dateRange.end,
          startType: typeof dateRange.start,
          endType: typeof dateRange.end,
          sampleSaleDate: sales[0]?.date,
          sampleSaleDateType: typeof sales[0]?.date
        });
        
        sales = sales.filter(sale => {
          if (!sale.date) {
            console.log('📊 JS FILTER DEBUG - Sale date is null/undefined:', sale);
            return false;
          }
          
          // Convert string date to Date object for comparison
          const saleDate = new Date(sale.date);
          const isWithinRange = saleDate >= dateRange.start && saleDate <= dateRange.end;
          
          if (!isWithinRange) {
            console.log('📊 JS FILTER DEBUG - Sale date out of range:', {
              saleDate: saleDate.toISOString(),
              rangeStart: dateRange.start.toISOString(),
              rangeEnd: dateRange.end.toISOString(),
              saleId: sale.id,
              partyName: sale.partyName
            });
          }
          return isWithinRange;
        });
        
        console.log('📊 PARTYWISE SALES DEBUG - After Date Filtering:', {
          originalCount: originalSalesCount,
          filteredCount: sales.length,
          dateRange: {
            start: dateRange.start.toISOString().split('T')[0],
            end: dateRange.end.toISOString().split('T')[0]
          }
        });
        
        // Filter by selected party if needed
        if (selectedParty) {
          sales = sales.filter(sale => sale.partyId === selectedParty);
        }
        // Fetch payments (company-scoped)
        const paymentsCollection = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
        
        const paymentsSnapshot = await getDocs(paymentsCollection);
        const payments = paymentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Only PRI (Payment Receipt Invoice) payments for sales reports
        const salesPayments = payments.filter(p => p.receiptNumber && p.receiptNumber.startsWith('PRI'));
        // Date-filtered for FIFO fallback (legacy payments without allocations)
        const filteredPayments = salesPayments.filter(p => {
          if (!p.paymentDate) return false;
          const payDate = new Date(p.paymentDate);
          return payDate >= dateRange.start && payDate <= dateRange.end;
        });
        // Helper to normalize bill/invoice/receipt numbers for matching
        function normalizeBillNumber(str) {
          if (!str) return '';
          return str.replace(/^(PRI|PRC|INV|ADV-INV|PRP|PRB)/, '').replace(/[^0-9/-]/g, '');
        }
        // Group sales by party
        const partyInvoices = {};
        sales.forEach(invoice => {
          const partyId = normalizePartyId(invoice.partyId);
          if (!partyInvoices[partyId]) {
            partyInvoices[partyId] = [];
          }
          partyInvoices[partyId].push(invoice);
        });
        
        console.log('📊 NORMALIZE PARTY ID DEBUG:', {
          samplePartyId: sales[0]?.partyId,
          normalizedPartyId: sales[0] ? normalizePartyId(sales[0].partyId) : null,
          partyIdToNameKeys: Object.keys(partyIdToName),
          samplePartyIdToName: sales[0] ? partyIdToName[normalizePartyId(sales[0].partyId)] : null
        });
        
        console.log('📊 PARTY GROUPING DEBUG:', {
          totalSales: sales.length,
          partyInvoices: Object.keys(partyInvoices).length,
          partyIds: Object.keys(partyInvoices),
          sampleParty: Object.keys(partyInvoices)[0] ? {
            partyId: Object.keys(partyInvoices)[0],
            invoiceCount: partyInvoices[Object.keys(partyInvoices)[0]].length,
            sampleInvoice: partyInvoices[Object.keys(partyInvoices)[0]][0]
          } : null
        });
        // Group ALL sales payments by party (not date-filtered) - for allocation-based calculation
        const allPartyPayments = {};
        salesPayments.forEach(payment => {
          const partyId = normalizePartyId(payment.partyId);
          if (!allPartyPayments[partyId]) {
            allPartyPayments[partyId] = [];
          }
          allPartyPayments[partyId].push(payment);
        });
        // Group date-filtered payments by party for FIFO fallback (legacy payments without allocations)
        const partyPayments = {};
        filteredPayments.forEach(payment => {
          const partyId = normalizePartyId(payment.partyId);
          if (!partyPayments[partyId]) {
            partyPayments[partyId] = [];
          }
          partyPayments[partyId].push(payment);
        });
        
        // Fetch party names for any missing parties
        const missingPartyIds = Object.keys(partyInvoices).filter(pid => !partyIdToName[pid] || partyIdToName[pid] === pid);
        const partyNamePromises = missingPartyIds.map(async (partyId) => {
          const name = await fetchPartyName(partyId);
          return { partyId, name };
        });
        const fetchedPartyNames = await Promise.all(partyNamePromises);
        const enhancedPartyIdToName = { ...partyIdToName };
        fetchedPartyNames.forEach(({ partyId, name }) => {
          if (name && name !== partyId) {
            enhancedPartyIdToName[partyId] = name;
          }
        });
        
        // Use stored allocations (same logic as Sales Bill List) for consistency across app
        const BILL_TYPE_INVOICE = 'invoice';
        const partySummary = {};
        Object.entries(partyInvoices).forEach(([partyId, invoices]) => {
          // Sort invoices by date
          const sortedInvoices = invoices.sort((a, b) => new Date(a.date) - new Date(b.date));
          
          // Step 1: Use allocations from ALL sales payments (not date-filtered) - matches Sales.js getBillOutstanding
          const partyAllPayments = (allPartyPayments[partyId] || []);
          sortedInvoices.forEach(inv => {
            let paidFromAllocations = 0;
            const receipts = [];
            partyAllPayments.forEach(payment => {
              if (!payment.allocations || !Array.isArray(payment.allocations)) return;
              const alloc = payment.allocations.find(a => 
                a.billId === inv.id && 
                (a.billType === BILL_TYPE_INVOICE || a.billType === 'Invoice' || !a.billType)
              );
              if (alloc && alloc.allocatedAmount) {
                const amt = Number(alloc.allocatedAmount) || 0;
                paidFromAllocations += amt;
                receipts.push({
                  receiptNumber: payment.receiptNumber,
                  amount: amt,
                  paymentDate: payment.paymentDate || payment.date
                });
              }
            });
            inv.paidAmount = paidFromAllocations;
            inv.paymentReceipts = receipts;
          });
          
          // Step 2: FIFO fallback for payments WITHOUT allocations (legacy/khata payments)
          const paymentsForFifo = (partyPayments[partyId] || [])
            .filter(p => !p.allocations || !p.allocations.some(a => 
              a.billType === BILL_TYPE_INVOICE || a.billType === 'Invoice'
            ))
            .sort((a, b) => new Date(a.paymentDate || a.date) - new Date(b.paymentDate || b.date));
          
          let invoicePointer = 0;
          let paymentPointer = 0;
          const paymentUsed = {}; // Track how much of each payment has been applied
          while (invoicePointer < sortedInvoices.length && paymentPointer < paymentsForFifo.length) {
            const currentInvoice = sortedInvoices[invoicePointer];
            const currentPayment = paymentsForFifo[paymentPointer];
            
            const invoiceOutstanding = Number(currentInvoice.totalAmount) - (currentInvoice.paidAmount || 0);
            const paymentTotal = Number(currentPayment.totalAmount || currentPayment.amount || currentPayment.paymentAmount || currentPayment.allocatedAmount || 0);
            const paymentRemaining = paymentTotal - (paymentUsed[currentPayment.id] || 0);
            
            if (invoiceOutstanding <= 0) {
              invoicePointer++;
              continue;
            }
            if (paymentRemaining <= 0) {
              paymentPointer++;
              continue;
            }
            
            const amountToApply = Math.min(paymentRemaining, invoiceOutstanding);
            
            currentInvoice.paidAmount = (currentInvoice.paidAmount || 0) + amountToApply;
            if (!currentInvoice.paymentReceipts) currentInvoice.paymentReceipts = [];
            currentInvoice.paymentReceipts.push({
              receiptNumber: currentPayment.receiptNumber,
              amount: amountToApply,
              paymentDate: currentPayment.paymentDate || currentPayment.date
            });
            
            paymentUsed[currentPayment.id] = (paymentUsed[currentPayment.id] || 0) + amountToApply;
            if (invoiceOutstanding <= amountToApply) invoicePointer++;
            if (paymentRemaining <= amountToApply) paymentPointer++;
          }
          
          const totalPaid = sortedInvoices.reduce((sum, inv) => sum + (inv.paidAmount || 0), 0);
          
          // Calculate totalAmount and totalGST
          let totalAmount = 0;
          let totalGST = 0;
          sortedInvoices.forEach(inv => {
            totalAmount += Number(inv.totalAmount);
            totalGST += inv.gst || 0;
          });
          
          // Outstanding/Advance
          let outstanding = totalAmount - totalPaid;
          let advance = 0;
          if (outstanding < 0) {
            advance = -outstanding;
            outstanding = 0;
          }
          
          
          // Find party name - use enhanced map with fetched names
          const partyName = enhancedPartyIdToName[partyId] || partyIdToName[partyId] || partyId;
          partySummary[partyId] = {
            partyId,
            partyName,
            totalInvoices: sortedInvoices.length,
            totalAmount,
            totalGST,
            totalPaid,
            totalOutstanding: outstanding,
            advance,
            lastInvoiceDate: sortedInvoices.length > 0 ? sortedInvoices[sortedInvoices.length - 1].date : null,
            invoices: sortedInvoices
          };
        });
        let reportDataArray = Object.values(partySummary);
        
        console.log('📊 PARTY SUMMARY DEBUG:', {
          partySummaryCount: Object.keys(partySummary).length,
          partySummaryKeys: Object.keys(partySummary),
          reportDataArrayLength: reportDataArray.length,
          samplePartySummary: reportDataArray[0] || null
        });
        
        // If a party is selected, only show that party's row
        if (selectedParty) {
          reportDataArray = reportDataArray.filter(row => row.partyId === selectedParty);
        }
        
        console.log('📊 FINAL REPORT DATA:', {
          finalReportDataLength: reportDataArray.length,
          finalReportData: reportDataArray
        });
        
        setReportData(reportDataArray);
        
        // Calculate totals
        let totals = reportDataArray.reduce((acc, party) => ({
          totalInvoices: acc.totalInvoices + party.totalInvoices,
          totalAmount: acc.totalAmount + party.totalAmount,
          totalGST: acc.totalGST + party.totalGST,
          totalPaid: acc.totalPaid + party.totalPaid,
          totalOutstanding: acc.totalOutstanding + party.totalOutstanding
        }), {
          totalInvoices: 0,
          totalAmount: 0,
          totalGST: 0,
          totalPaid: 0,
          totalOutstanding: 0
        });
        

        // Add composition GST if applicable
        if (fetchedCompanyDetails && fetchedCompanyDetails.gstType === 'composition') {
          const compositionRate = fetchedCompanyDetails.compositionRate || 1; // Default 1% for goods
          const compositionGST = (totals.totalAmount * compositionRate) / 100;
          totals.totalGST = compositionGST;
        }

        setTotalSummary(totals);
      } catch (error) {
        console.error('Error fetching partywise sales data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [db, userId, appId, companyId, dateRange, selectedParty, setLoading, companyDetails, parties, partyIdToName]);

  // Find selected party object
  const selectedPartyObj = parties.find(p => p.id === selectedParty);

  // Table columns
  const columns = [
    { key: 'partyName', label: 'Party Name' },
    { key: 'totalInvoices', label: 'Total Invoices' },
    { key: 'totalAmount', label: 'Total Amount', render: formatCurrency },
    { key: 'totalGST', label: 'Total GST', render: formatCurrency },
    { key: 'totalPaid', label: 'Total Paid', render: formatCurrency },
    { key: 'totalOutstanding', label: 'Outstanding', render: formatCurrency },
    { key: 'lastInvoiceDate', label: 'Last Invoice Date', render: v => v ? formatDate(v) : '-' }
  ];

  // Handle party row click for expansion
  const handlePartyRowClick = (row) => {
    if (expandedParty === row.partyId) {
      setExpandedParty(null);
    } else {
      setExpandedParty(row.partyId);
    }
  };

  // Handle invoice click for dropdown
  const handleInvoiceClick = async (invoice) => {
    if (expandedInvoice === invoice.id) {
      setExpandedInvoice(null);
    } else {
      setExpandedInvoice(invoice.id);
      
      // Fetch item names for this invoice
      if (invoice.items && invoice.items.length > 0) {
        const itemIds = invoice.items.map(item => item.item || item.id || item.itemId || item.productId).filter(Boolean);
        if (itemIds.length > 0) {
          const names = await fetchItemNames(itemIds);
          setItemNames(prevNames => ({ ...prevNames, ...names }));
        }
      } else {
        // Reset item names if no items
        setItemNames({});
      }
    }
  };

  return (
    <div className="p-3 sm:p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-2">Partywise Sales / Sales Summary</h2>
        <p className="text-gray-600">
          Period: {formatDate(dateRange.start)} to {formatDate(dateRange.end)}
          {selectedParty && ` | Party: ${selectedPartyObj ? selectedPartyObj.firmName : selectedParty}`}
        </p>
      </div>
      <CollapsibleQuickTotals
        cards={[
          { label: 'Total Invoices', value: totalSummary.totalInvoices, color: 'blue' },
          { label: 'Total Amount', value: formatCurrency(totalSummary.totalAmount), color: 'green' },
          { label: 'Total GST', value: formatCurrency(totalSummary.totalGST), color: 'purple' },
          { label: 'Total Paid', value: formatCurrency(totalSummary.totalPaid), color: 'yellow' },
          { label: 'Outstanding', value: formatCurrency(totalSummary.totalOutstanding), color: 'red' }
        ]}
      />
      <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm overflow-hidden">
        {/* Mobile Card View - Responsive */}
        <div className="block lg:hidden">
          {reportData.length === 0 ? (
            <div className="text-center py-8 px-4">
              <div className="text-gray-400 mb-2">No data found</div>
              <div className="text-sm text-gray-500">
                Period: {formatDate(dateRange.start)} to {formatDate(dateRange.end)}
                {selectedParty && ` | Party: ${selectedPartyObj ? selectedPartyObj.firmName : selectedParty}`}
              </div>
              <div className="text-xs text-gray-400 mt-2">
                Try adjusting the date range or party filter above
              </div>
            </div>
          ) : (
            <div className="p-3 space-y-3">
              {reportData.map((row, index) => (
                <div key={row.partyId || index} className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200/90 shadow-sm overflow-hidden">
                  <div
                    className="p-3.5 cursor-pointer active:bg-slate-50/80 transition-colors"
                    onClick={() => handlePartyRowClick(row)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-amber-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">
                        {(row.partyName || 'P').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate">{row.partyName}</div>
                        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                          <FileText className="w-3 h-3 flex-shrink-0" />
                          {row.totalInvoices} bill{row.totalInvoices === 1 ? '' : 's'}
                          {row.lastInvoiceDate && (
                            <>
                              <span className="text-gray-300 mx-0.5">·</span>
                              <Calendar className="w-3 h-3 flex-shrink-0" />
                              {formatDate(row.lastInvoiceDate)}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span
                          className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                            row.totalOutstanding > 0 ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          {row.totalOutstanding > 0 ? 'Outstanding' : 'Settled'}
                        </span>
                        {expandedParty === row.partyId ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" aria-hidden />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden />
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div className="bg-white/90 p-2.5 rounded-lg border border-slate-100 shadow-sm">
                        <div className="text-[10px] text-gray-500 uppercase tracking-wide">Total</div>
                        <div className="text-sm font-bold text-gray-900">{formatCurrency(row.totalAmount)}</div>
                      </div>
                      <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-2.5 rounded-lg border border-violet-100 shadow-sm">
                        <div className="text-[10px] text-violet-600 font-medium uppercase tracking-wide">GST</div>
                        <div className="text-sm font-bold text-violet-900">{formatCurrency(row.totalGST)}</div>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-2.5 rounded-lg border border-green-200 shadow-sm">
                        <div className="flex items-center gap-0.5 text-[10px] text-green-700 font-medium uppercase tracking-wide">
                          <TrendingUp className="w-3 h-3" /> Paid
                        </div>
                        <div className="text-sm font-bold text-green-700">{formatCurrency(row.totalPaid)}</div>
                      </div>
                      <div className="bg-gradient-to-br from-red-50 to-rose-50 p-2.5 rounded-lg border border-red-100 shadow-sm">
                        <div className="flex items-center gap-0.5 text-[10px] text-red-700 font-medium uppercase tracking-wide">
                          <TrendingDown className="w-3 h-3" /> Out
                        </div>
                        <div className="text-sm font-bold text-red-700">{formatCurrency(row.totalOutstanding)}</div>
                      </div>
                    </div>
                  </div>

                  {expandedParty === row.partyId && (
                    <div className="border-t border-slate-100 bg-slate-50/60 p-3 space-y-2">
                      <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wide flex items-center gap-1.5">
                        <Receipt className="w-3.5 h-3.5 text-blue-600" />
                        Invoices — {row.partyName}
                      </h4>
                      {(row.invoices || []).map((inv, i) => (
                        <div
                          key={inv.id || i}
                          className="bg-gradient-to-br from-white to-slate-50 rounded-xl border border-slate-200/90 shadow-sm overflow-hidden"
                        >
                          <div className="p-3">
                            <div className="flex justify-between items-start gap-2 mb-2">
                              <div className="flex-1 min-w-0">
                                <button
                                  type="button"
                                  className="text-left text-blue-700 font-semibold text-sm flex items-center gap-1.5 hover:text-blue-900"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleInvoiceClick(inv);
                                  }}
                                >
                                  <span className="truncate">{inv.invoiceNumber}</span>
                                  {expandedInvoice === inv.id ? (
                                    <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden />
                                  )}
                                </button>
                                <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {inv.date ? formatDate(inv.date) : '—'}
                                </div>
                              </div>
                              <div className="text-right text-sm flex-shrink-0">
                                <div className="font-bold text-gray-900">{formatCurrency(inv.totalAmount)}</div>
                                <div className="text-green-600 text-xs font-medium">
                                  Paid {formatCurrency(inv.paidAmount || 0)}
                                </div>
                              </div>
                            </div>

                            {inv.paymentReceipts && inv.paymentReceipts.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-slate-100">
                                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                  Receipts
                                </div>
                                <div className="space-y-1">
                                  {inv.paymentReceipts.map((receipt, idx) => (
                                    <div
                                      key={idx}
                                      className="text-xs text-green-700 bg-green-50/80 rounded-lg px-2 py-1 border border-green-100"
                                    >
                                      {receipt.receiptNumber} ({formatCurrency(receipt.amount)})
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {expandedInvoice === inv.id && inv.items && inv.items.length > 0 && (
                            <div className="border-t border-slate-100 bg-sky-50/40 p-3">
                              <h5 className="text-[10px] font-bold text-sky-900 uppercase tracking-wide mb-2">
                                Line items
                              </h5>
                              <div className="space-y-2">
                                {inv.items.map((item, itemIdx) => {
                                  const itemId = item.item || item.id || item.itemId || item.productId;
                                  const itemName =
                                    itemNames[itemId] ||
                                    item.itemName ||
                                    item.name ||
                                    item.productName ||
                                    item.product ||
                                    itemId ||
                                    `Item ${itemIdx + 1}`;
                                  return (
                                    <div
                                      key={itemIdx}
                                      className="bg-white rounded-lg p-2.5 border border-slate-200 shadow-sm"
                                    >
                                      <div className="flex justify-between items-start gap-2">
                                        <div className="flex-1 min-w-0">
                                          <div className="font-semibold text-sm text-gray-900">{itemName}</div>
                                          {(item.dimensions || item.description) && (
                                            <div className="text-xs text-gray-500 mt-1">
                                              {item.dimensions || item.description}
                                            </div>
                                          )}
                                        </div>
                                        <div className="text-right text-xs sm:text-sm flex-shrink-0">
                                          <div className="text-gray-600">
                                            Qty {item.qty || item.quantity || 0}
                                          </div>
                                          <div className="text-gray-600">
                                            {formatCurrencyIntl(item.rate || item.price || item.cost || 0, companyCurrency)}
                                          </div>
                                          <div className="font-bold text-gray-900">
                                            {formatCurrencyIntl(item.amount || 0, companyCurrency)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table id="report-table" className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
              {columns.map(col => (
                <th key={col.key} className={`px-2 sm:px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  col.key === 'partyName' ? 'min-w-[120px]' : 
                  col.key === 'totalInvoices' ? 'w-24' :
                  col.key === 'totalAmount' ? 'min-w-[100px]' :
                  col.key === 'totalGST' ? 'min-w-[100px]' :
                  col.key === 'totalPaid' ? 'min-w-[100px]' :
                  col.key === 'totalOutstanding' ? 'min-w-[100px]' :
                  col.key === 'lastInvoiceDate' ? 'min-w-[120px]' : ''
                }`}>{col.label}</th>
              ))}
              <th className="px-2 sm:px-3 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Details</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
            {reportData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center py-8">
                  <div className="text-gray-400 mb-2">No data found</div>
                  <div className="text-sm text-gray-500">
                    Period: {formatDate(dateRange.start)} to {formatDate(dateRange.end)}
                    {selectedParty && ` | Party: ${selectedPartyObj ? selectedPartyObj.firmName : selectedParty}`}
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    Try adjusting the date range or party filter above
                  </div>
                </td>
              </tr>
            ) : reportData.map((row, idx) => (
              <React.Fragment key={row.partyId || idx}>
                <tr className="cursor-pointer hover:bg-blue-50" onClick={() => handlePartyRowClick(row)}>
                  {columns.map(col => (
                    <td key={col.key} className={`px-2 sm:px-3 lg:px-6 py-4 ${
                      col.key === 'partyName' ? 'min-w-[120px] truncate' : 
                      col.key === 'totalInvoices' ? 'w-24 text-center' :
                      col.key === 'totalAmount' ? 'min-w-[100px] text-right' :
                      col.key === 'totalGST' ? 'min-w-[100px] text-right' :
                      col.key === 'totalPaid' ? 'min-w-[100px] text-right' :
                      col.key === 'totalOutstanding' ? 'min-w-[100px] text-right' :
                      col.key === 'lastInvoiceDate' ? 'min-w-[120px]' : 'whitespace-nowrap'
                    }`}>
                      {col.render ? col.render(row[col.key], row) : row[col.key]}
                    </td>
                  ))}
                  <td className="px-2 sm:px-3 lg:px-6 py-4 text-center w-16">
                    <span className="inline-flex text-gray-400 justify-center">
                      {expandedParty === row.partyId ? (
                        <ChevronUp className="w-4 h-4" aria-hidden />
                      ) : (
                        <ChevronDown className="w-4 h-4" aria-hidden />
                      )}
                    </span>
                  </td>
                </tr>
                
                {/* Expanded Row - Invoice Details */}
                {expandedParty === row.partyId && (
                  <tr>
                    <td colSpan={columns.length + 1} className="px-2 sm:px-3 lg:px-6 py-4 bg-gray-50">
                      <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200 shadow-sm">
                        <h4 className="text-sm font-semibold text-gray-800 mb-3">Invoices for {row.partyName}:</h4>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[120px]">Invoice No</th>
                                <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[100px]">Date</th>
                                <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[100px]">Amount</th>
                                <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[100px]">Paid Amount</th>
                                <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[120px]">Payment Receipts</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {(row.invoices || []).map((inv, i) => (
                                <React.Fragment key={inv.id || i}>
                                  <tr className="hover:bg-gray-50">
                                    <td className="px-2 sm:px-4 py-2 min-w-[120px]">
                                      <button 
                                        className="text-blue-600 hover:text-blue-800 underline font-medium text-sm flex items-center"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleInvoiceClick(inv);
                                        }}
                                      >
                                        <span className="truncate">{inv.invoiceNumber}</span>
                                        <span className="ml-2 text-gray-400 flex-shrink-0 inline-flex">
                                          {expandedInvoice === inv.id ? (
                                            <ChevronUp className="w-4 h-4" aria-hidden />
                                          ) : (
                                            <ChevronDown className="w-4 h-4" aria-hidden />
                                          )}
                                        </span>
                                      </button>
                                    </td>
                                    <td className="px-2 sm:px-4 py-2 text-sm text-gray-900 min-w-[100px]">
                                      {inv.date ? formatDate(inv.date) : '-'}
                                    </td>
                                    <td className="px-2 sm:px-4 py-2 text-sm font-medium text-gray-900 text-right min-w-[100px]">
                                      {formatCurrency(inv.totalAmount)}
                                    </td>
                                    <td className="px-2 sm:px-4 py-2 text-sm text-gray-900 text-right min-w-[100px]">
                                      {formatCurrency(inv.paidAmount || 0)}
                                    </td>
                                    <td className="px-2 sm:px-4 py-2 text-sm text-gray-900 min-w-[120px]">
                                      {inv.paymentReceipts && inv.paymentReceipts.length > 0 ? (
                                        <div className="space-y-1">
                                          {inv.paymentReceipts.map((receipt, idx) => (
                                            <div key={idx} className="text-green-600 text-xs">
                                              {receipt.receiptNumber} ({formatCurrency(receipt.amount)})
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <span className="text-gray-400 text-xs">No receipts</span>
                                      )}
                                    </td>
                                  </tr>
                                  
                                  {/* Invoice Items Dropdown Row */}
                                  {expandedInvoice === inv.id && inv.items && inv.items.length > 0 && (
                                    <tr>
                                      <td colSpan="5" className="px-2 sm:px-4 py-2 bg-gray-50">
                                        <div className="bg-white rounded-lg p-2 sm:p-3 border border-gray-200">
                                          <h5 className="text-xs font-semibold text-gray-700 mb-2">Items in this invoice:</h5>
                                          <div className="space-y-2">
                                            {inv.items.map((item, itemIdx) => {
                                              const itemId = item.item || item.id || item.itemId || item.productId;
                                              const itemName = itemNames[itemId] || item.itemName || item.name || item.productName || item.product || itemId || `Item ${itemIdx + 1}`;
                                              return (
                                                <div key={itemIdx} className="bg-gray-50 rounded p-2 border border-gray-200">
                                                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                                                    <div className="flex-1 min-w-0">
                                                      <div className="font-medium text-sm text-gray-900 truncate">{itemName}</div>
                                                      {(item.dimensions || item.description) && (
                                                        <div className="text-xs text-gray-500 mt-1 truncate">
                                                          {item.dimensions || item.description}
                                                        </div>
                                                      )}
                                                    </div>
                                                    <div className="text-right text-sm flex-shrink-0 w-full sm:w-auto">
                                                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                                                        <div className="text-gray-600">
                                                          <span className="hidden sm:inline">Qty: </span>{item.qty || item.quantity || 0}
                                                        </div>
                                                        <div className="text-gray-600">
                                                          <span className="hidden sm:inline">Rate: </span>{formatCurrencyIntl(item.rate || item.price || item.cost || 0, companyCurrency)}
                                                        </div>
                                                        <div className="font-medium text-gray-900">
                                                          <span className="hidden sm:inline">Total: </span>{formatCurrencyIntl(item.amount || 0, companyCurrency)}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      
    </div>
  );
};

export default PartywiseSalesReport; 